import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { adsPlatformSearch, AdsDoctorSalonPage, useAdsDoctorSalonLookup, type AdsPlatform } from '@/features/agency';
import { formatDocumentTitle } from '@/lib/document-title';

interface AdsDoctorSalonSearch {
  /** `google` = the salon's Google check-up; omitted = Meta. Never set to undefined (TanStack would keep the key). */
  platform?: 'google';
}

/** One salon's Ads Doctor check-up; the API answers 404 for a salon outside the member's scope. */
export const Route = createFileRoute('/_auth/agency/ads-doctor/$shortCode')({
  validateSearch: (search: Record<string, unknown>): AdsDoctorSalonSearch => (search.platform === 'google' ? { platform: 'google' } : {}),
  component: AdsDoctorSalonRoute,
});

function AdsDoctorSalonRoute() {
  const { shortCode } = Route.useParams();
  const platform: AdsPlatform = Route.useSearch().platform ?? 'meta';
  const navigate = useNavigate({ from: Route.fullPath });
  // Same by-short-code read as the page (deduped): titles the tab with the salon once it loads.
  const { name } = useAdsDoctorSalonLookup(shortCode);
  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle(name ? `${name} · Ads Doctor` : 'Ads Doctor')}</title>
      </Helmet>
      {/* Keyed by salon: per-salon state (an open finding, a queued check) never carries over to another salon. */}
      <AdsDoctorSalonPage
        key={shortCode}
        shortCode={shortCode}
        platform={platform}
        onPlatformChange={(next) => void navigate({ search: adsPlatformSearch(next), replace: true })}
      />
    </>
  );
}
